import React, { useCallback } from 'react'
import { Trans } from 'react-i18next'

import {
  COND_FEATURE_FLAG_ALL,
  ConditionBooleanOperator,
  ConditionExpression,
  ConditionFeatureFlag,
  ConditionNumericOperator,
  ConditionStatementCustomText,
  ConditionValue,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_INPUT_RHS,
} from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { Option } from '@/shared/types'
import { ConditionOperatorSelectV2 } from '@/components/BehaviourV2/ConditionOperatorSelectV2'
import { ConditionRepeaterContext } from '@/components/BehaviourV2/ConditionRepeaterContext'
import { ConditionSelectV2 } from '@/components/BehaviourV2/ConditionSelectV2'

interface SwitchConditionItemProps {
  id: string
  value: ConditionValue
  onChange?: (nextValue: ConditionValue) => void
  pathName: string
  pathColor: { dot: string; text: string; border: string }
  onPathNameChange: (name: string) => void
  onPathNameEdit: () => void
  isEditingPathName: boolean
  onRemoveCase: () => void
  canRemove: boolean
  actions?: Option[]
}

function SwitchConditionItem({
  id,
  value,
  onChange,
  pathName,
  pathColor,
  onPathNameChange,
  onPathNameEdit,
  isEditingPathName,
  onRemoveCase,
  canRemove,
  actions,
}: SwitchConditionItemProps) {
  const cond = value.conditions[0] // Only use the first condition

  const handleOperatorChange = (opt: DefaultConditionOperator) => {
    const nextCond = [...value.conditions]
    const currentCondition = nextCond[0]
    const currentOp = currentCondition.operator

    // If the operator is the same, do nothing
    if (currentOp === opt) {
      return
    }

    // Check operator types to determine if we can preserve values
    const isCurrentRange = [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(currentOp)
    const isNewRange = [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(opt)
    const isCurrentDate = [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS].includes(currentOp)
    const isNewDate = [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS].includes(opt)
    const currentNeedsRhs =
      OPERATOR_WITH_INPUT_RHS.includes(currentOp) ||
      [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(
        currentOp
      )
    const newNeedsRhs =
      OPERATOR_WITH_INPUT_RHS.includes(opt) ||
      [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(opt)

    let nextRhs = currentCondition.rhs
    let nextRhsEnd = undefined

    // Handle different transition scenarios
    if (!newNeedsRhs) {
      // New operator doesn't need RHS - clear it
      nextRhs = undefined
    } else if (!currentNeedsRhs) {
      // Previous operator didn't need RHS, but new one does - create appropriate default
      if (isNewDate) {
        nextRhs = {
          flag: ConditionFeatureFlag.CustomText,
          value: new Date().toISOString(),
        } as ConditionStatementCustomText
      } else {
        nextRhs = {
          flag: ConditionFeatureFlag.CustomText,
          value: '',
        } as ConditionStatementCustomText
      }
    } else {
      // Both operators need RHS - try to preserve value

      if (isCurrentRange && !isNewRange) {
        // Switching from range to single: keep the start value
        nextRhs = currentCondition.rhs
      } else if (!isCurrentRange && isNewRange) {
        // Switching from single to range: duplicate the value for both start and end
        nextRhs = currentCondition.rhs
        nextRhsEnd = currentCondition.rhs ? { ...currentCondition.rhs } : undefined
      } else if (isCurrentRange && isNewRange) {
        // Both are range operators: preserve both values
        nextRhs = currentCondition.rhs
        nextRhsEnd = (currentCondition as any).endValue
          ? ({
              flag: ConditionFeatureFlag.CustomText,
              value: (currentCondition as any).endValue,
            } as ConditionStatementCustomText)
          : undefined
      } else if (isCurrentDate !== isNewDate) {
        // Switching between date and non-date: need to reset for type safety
        if (isNewDate) {
          nextRhs = {
            flag: ConditionFeatureFlag.CustomText,
            value: new Date().toISOString(),
          } as ConditionStatementCustomText
        } else {
          // Try to preserve the value if it's not a date-specific format
          nextRhs = {
            flag: ConditionFeatureFlag.CustomText,
            // @ts-ignore
            value: currentCondition.rhs?.value || '',
          } as ConditionStatementCustomText
        }
      } else {
        // Same type of operator (both single, both need RHS) - preserve value
        nextRhs = currentCondition.rhs
      }
    }

    // Handle range end value for new range operators
    if (isNewRange && !nextRhsEnd) {
      if (isNewDate) {
        nextRhsEnd = {
          flag: ConditionFeatureFlag.CustomText,
          value: new Date().toISOString(),
        } as ConditionStatementCustomText
      } else {
        nextRhsEnd = {
          flag: ConditionFeatureFlag.CustomText,
          // @ts-ignore
          value: currentCondition.rhs?.value || '',
        } as ConditionStatementCustomText
      }
    }

    nextCond[0] = {
      ...nextCond[0],
      operator: opt,
      rhs: nextRhs,
      // Add endValue for range operators
      ...(isNewRange ? { endValue: nextRhsEnd } : {}),
    }

    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: nextCond,
      })
    }
  }
  const shouldRenderRHS =
    OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ||
    DATE_OPERATOR_WITH_RHS.includes(cond.operator) ||
    [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(cond.operator)
  const shouldRenderRange = [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(
    cond.operator as ConditionNumericOperator
  )

  const gridColumn = shouldRenderRHS && !shouldRenderRange ? '1fr 1fr 1fr' : '1fr 1fr'

  return (
    <div className='flex items-center gap-2 w-full'>
      {/* Path indicator and name - Fixed width for alignment */}
      <div className='flex items-center gap-2 w-24 flex-shrink-0'>
        <div className={`w-3 h-3 ${pathColor.dot} rounded-full flex-shrink-0`}></div>
        {isEditingPathName ? (
          <input
            maxLength={12}
            type='text'
            value={pathName}
            onChange={(e) => onPathNameChange(e.target.value)}
            onBlur={() => onPathNameEdit()}
            onKeyDown={(e) => e.key === 'Enter' && onPathNameEdit()}
            className={`w-full bg-white dark:bg-gray-800 ${pathColor.text} px-1 py-0 rounded border border-gray-300 dark:border-gray-600 outline-none text-sm focus:border-blue-500`}
            autoFocus
          />
        ) : (
          <span
            className={`${pathColor.text} text-sm font-medium cursor-pointer hover:opacity-80 border-b border-dashed ${pathColor.border}`}
            onClick={onPathNameEdit}
          >
            {pathName}
          </span>
        )}
      </div>

      {/* Condition row - horizontal layout */}
      <div className='items-center gap-2 grid w-full' style={{ gridTemplateColumns: gridColumn }}>
        {/* Left Hand Side - Condition Selection */}
        <div className='flex-1'>
          <ConditionSelectV2 id={`${id}-lhs`} placeholder='Select condition...' index={0} actions={actions} />
        </div>

        {/* Operator Selection */}
        <div className='min-w-[140px]'>
          <ConditionOperatorSelectV2
            id={id ? `${id}-op` : undefined}
            index={0}
            selected={cond.operator}
            value={value}
            onChange={handleOperatorChange}
          />
        </div>

        {/* Right Hand Side - Workflow Condition Selection or Custom Value */}
        {shouldRenderRHS && (
          <div
            className='flex-1 flex-col flex gap-2'
            style={{ gridColumn: shouldRenderRange ? 'span 2 / span 2' : 'span 1 / span 1' }}
          >
            {/* Only render ConditionSelectV2 when it makes sense for the operator */}
            {OPERATOR_WITH_INPUT_RHS.includes(cond.operator) &&
              ![...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(cond.operator) && (
                <ConditionSelectV2 id={`${id}-rhs`} placeholder='Value' isRhs={true} index={0} actions={actions} />
              )}

            {/* Date/Time Input */}
            {DATE_OPERATOR_WITH_RHS.includes(cond.operator) && (
              <ConditionSelectV2 id={`${id}-rhs-date`} isRhs={true} index={0} isDate={true} actions={actions} />
            )}

            {/* Numeric Range Input */}
            {[...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(
              cond.operator as ConditionNumericOperator
            ) && (
              <div className='gap-2 w-full'>
                <div className=''>
                  <div className='uppercase text-m-ss'>
                    <Trans>Lower Bound</Trans>
                  </div>
                  <ConditionSelectV2
                    key={`${id}-rhs-start`}
                    id={id ? `${id}-rhs-start` : undefined}
                    isRhs={true}
                    index={0}
                    isDate={true}
                  />
                </div>
                <div className=''>
                  <div className='uppercase text-m-ss'>
                    <Trans>Upper Bound</Trans>
                  </div>
                  <ConditionSelectV2
                    key={`${id}-rhs-end`}
                    id={id ? `${id}-rhs-end` : undefined}
                    isRhs={true}
                    index={0}
                    isEnd
                    isDate={true}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Remove case button */}
      {canRemove && (
        <button onClick={onRemoveCase} className='cursor-pointer flex items-center justify-center'>
          <MaterialIcon icon={'close'} size={18} />
        </button>
      )}
    </div>
  )
}

interface SwitchConditionRepeaterProps {
  id?: string
  value: ConditionValue
  onChange?: (nextValue: ConditionValue) => void
  pathName: string
  pathColor: { dot: string; text: string; border: string }
  onPathNameChange: (name: string) => void
  onPathNameEdit: () => void
  isEditingPathName: boolean
  onRemoveCase: () => void
  canRemove: boolean
  actions?: Option[]
}

export function SwitchConditionRepeater({
  id,
  value,
  onChange,
  pathName,
  pathColor,
  onPathNameChange,
  onPathNameEdit,
  isEditingPathName,
  onRemoveCase,
  canRemove,
  actions,
}: SwitchConditionRepeaterProps) {
  // Ensure there's always at least one condition
  const ensureSingleCondition = () => {
    if (!value.conditions || value.conditions.length === 0) {
      const defaultCondition: ConditionExpression = {
        lhs: { flag: ConditionFeatureFlag.UserLoggedIn },
        operator: ConditionBooleanOperator.IsFalse,
      }

      if (onChange) {
        onChange({
          isAny: value.isAny,
          conditions: [defaultCondition],
        })
      }
    }
  }

  // Call this on mount to ensure we have a condition
  React.useEffect(() => {
    ensureSingleCondition()
  }, [])

  // If no conditions exist, show a placeholder
  if (!value.conditions || value.conditions.length === 0) {
    return (
      <div className='flex items-center gap-2 w-full'>
        {/* Path indicator and name */}
        <div className='flex items-center gap-2 w-24 flex-shrink-0'>
          <div className={`w-3 h-3 ${pathColor.dot} rounded-full flex-shrink-0`}></div>
          {isEditingPathName ? (
            <input
              type='text'
              value={pathName}
              onChange={(e) => onPathNameChange(e.target.value)}
              onBlur={() => onPathNameEdit()}
              onKeyDown={(e) => e.key === 'Enter' && onPathNameEdit()}
              className={`w-full bg-white dark:bg-gray-800 ${pathColor.text} px-1 py-0 rounded border border-gray-300 dark:border-gray-600 outline-none text-sm focus:border-blue-500`}
              autoFocus
            />
          ) : (
            <span
              className={`${pathColor.text} text-sm font-medium cursor-pointer hover:opacity-80 border-b border-dashed ${pathColor.border}`}
              onClick={onPathNameEdit}
            >
              {pathName}
            </span>
          )}
        </div>

        {/* Placeholder for condition */}
        <div className='flex-1 flex items-center gap-2'>
          <div className='flex-1 text-gray-400 dark:text-gray-500 text-sm italic'>
            <Trans>Click to add condition</Trans>
          </div>
        </div>

        <button onClick={onRemoveCase} className='cursor-pointer flex items-center justify-center'>
          <MaterialIcon icon={'close'} size={18} />
        </button>
      </div>
    )
  }

  return (
    <ConditionRepeaterContext.Provider
      value={{
        id,
        value,
        onChange,
        featureFlags: COND_FEATURE_FLAG_ALL | ConditionFeatureFlag.ActionOutput,
        parentFormId: undefined,
      }}
    >
      <SwitchConditionItem
        id={id || 'switch-condition'}
        value={value}
        onChange={onChange}
        pathName={pathName}
        pathColor={pathColor}
        onPathNameChange={onPathNameChange}
        onPathNameEdit={onPathNameEdit}
        isEditingPathName={isEditingPathName}
        onRemoveCase={onRemoveCase}
        canRemove={canRemove}
        actions={actions}
      />
    </ConditionRepeaterContext.Provider>
  )
}
